/** Display helpers for the internal attendance log (clock in/out with logged location). */

export interface AttendancePunchLike {
  latitude: number | null;
  longitude: number | null;
  locationStatus: string | null;
  hubDistanceM: number | null;
  hubGeofenceMatch: boolean | null;
}

export type PunchBadgeTone = 'ok' | 'away' | 'none';

export interface PunchBadge {
  /** StatusBadge colour key. */
  status: 'available' | 'maintenance' | 'pending' | 'inactive';
  label: string;
  tone: PunchBadgeTone;
}

/** "120 m", "2.3 km". */
export function formatDistance(metres: number | null | undefined): string {
  if (metres == null || !Number.isFinite(metres)) return '—';
  if (metres < 1000) return `${Math.round(metres)} m`;
  return `${(metres / 1000).toFixed(1)} km`;
}

export function hasLocation(p: AttendancePunchLike): boolean {
  return p.latitude != null && p.longitude != null;
}

export function mapsLink(p: AttendancePunchLike): string | null {
  if (!hasLocation(p)) return null;
  return `https://www.google.com/maps/search/?api=1&query=${p.latitude},${p.longitude}`;
}

/** Badge for one clock in/out: at hub / away from hub (distance) / no location (why). */
export function punchBadge(p: AttendancePunchLike): PunchBadge | null {
  if (!hasLocation(p)) {
    const why = p.locationStatus === 'denied' ? 'denied' : p.locationStatus === 'unavailable' ? 'unavailable' : null;
    return { status: 'pending', label: why ? `No location (${why})` : 'No location', tone: 'none' };
  }
  if (p.hubGeofenceMatch === false) {
    return {
      status: 'maintenance',
      label: p.hubDistanceM != null ? `Away from hub (${formatDistance(p.hubDistanceM)})` : 'Away from hub',
      tone: 'away',
    };
  }
  if (p.hubGeofenceMatch === true) return { status: 'available', label: 'At hub', tone: 'ok' };
  return null;
}

/** "Manila YYYY-MM-DD" n days before `ymd`. */
export function shiftYmd(ymd: string, days: number): string {
  const d = new Date(`${ymd}T12:00:00+08:00`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toLocaleDateString('en-CA', { timeZone: 'Asia/Manila' });
}

/** Clock time only, "8:05 AM" (Manila). */
export function formatClockTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString('en-PH', { timeZone: 'Asia/Manila', hour: 'numeric', minute: '2-digit' });
}

/** Shift length "8 h 05 min" between clock in and out. */
export function formatShiftLength(inIso: string | null | undefined, outIso: string | null | undefined): string {
  if (!inIso || !outIso) return '—';
  const mins = Math.round((new Date(outIso).getTime() - new Date(inIso).getTime()) / 60000);
  if (!Number.isFinite(mins) || mins < 0) return '—';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h ? `${h} h ${String(m).padStart(2, '0')} min` : `${m} min`;
}
